import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { normalizeRoleName } from "../utils/roles";

const toLocalDateKey = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const normalizeDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const parsed = new Date(value);
  return toLocalDateKey(parsed);
};

const monthMatrix = (year, monthIndex0) => {
  const first = new Date(year, monthIndex0, 1);
  const last = new Date(year, monthIndex0 + 1, 0);
  const daysInMonth = last.getDate();
  const startDow = (first.getDay() + 6) % 7; // Monday=0

  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, monthIndex0, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const isWeekend = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return false;
  const day = dateObj.getDay(); // Sun=0..Sat=6
  return day === 0 || day === 6;
};

const holidayShortLabel = (name) => {
  if (!name) return "";
  const trimmed = String(name).trim();
  if (!trimmed) return "";
  const firstWord = trimmed.split(" ")[0];
  return firstWord.length > 3 ? `${firstWord.slice(0, 3)}…` : firstWord;
};

const TeamLeaveCalendarCard = ({ me }) => {
  const { t, lang } = useI18n();
  const safeT = (key, vars, fallback) => {
    const value = t(key, vars);
    return value === key ? fallback : value;
  };

  const role = normalizeRoleName(me?.role_name);
  const canSee =
    role === "admin" || role === "company_manager" || role === "hr" || role === "unit_supervisor";
  const scopeLabel =
    role === "unit_supervisor"
      ? safeT("team.calendarScopeUnit", null, "Birimimdeki onaylı izinler")
      : safeT("team.calendarScopeCompany", null, "Şirket genelindeki onaylı izinler");

  const [calendarStart, setCalendarStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [counts, setCounts] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const countMap = useMemo(() => {
    const map = new Map();
    (counts || []).forEach((row) => {
      const key = String(row?.date || "").slice(0, 10);
      const value = Number(row?.count || 0);
      if (key) map.set(key, Number.isFinite(value) ? value : 0);
    });
    return map;
  }, [counts]);

  const holidayMap = useMemo(() => {
    const map = new Map();
    (publicHolidays || []).forEach((h) => {
      const key = normalizeDateKey(h?.date);
      if (key) map.set(key, h.name);
    });
    return map;
  }, [publicHolidays]);

  const todayKey = useMemo(() => toLocalDateKey(new Date()), []);

  const monthRangeLabel = useMemo(() => {
    const start = new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1);
    const end = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + 4, 1);
    const locale = lang === "tr" ? "tr-TR" : "en-US";
    const startLabel = start.toLocaleDateString(locale, { month: "long", year: "numeric" });
    const endLabel = end.toLocaleDateString(locale, { month: "long", year: "numeric" });
    return `${startLabel} - ${endLabel}`;
  }, [calendarStart, lang]);

  useEffect(() => {
    if (!canSee) return;

    const start = new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1);
    const end = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + 5, 0);
    const startKey = toLocalDateKey(start);
    const endKey = toLocalDateKey(end);

    // Hızlı ay değişiminde eski isteğin cevabı yenisini ezmesin
    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      apiClient.get("/admin/leaves/calendar", { params: { start: startKey, end: endKey } }),
      apiClient.get("/public-holidays", { params: { start: startKey, end: endKey } }),
    ])
      .then(([calRes, holidayRes]) => {
        if (cancelled) return;
        setCounts(Array.isArray(calRes.data?.days) ? calRes.data.days : []);
        setPublicHolidays(holidayRes.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCounts([]);
        setPublicHolidays([]);
        setError(err.response?.data?.msg || err.message || t("common.genericError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [calendarStart, canSee, t]);

  if (!canSee) return null;

  const locale = lang === "tr" ? "tr-TR" : "en-US";
  const weekdays =
    lang === "tr" ? ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const months = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + i, 1);
    return {
      key: `${d.getFullYear()}-${d.getMonth()}`,
      date: d,
      label: d.toLocaleDateString(locale, { month: "long", year: "numeric" }),
    };
  });

  return (
    <div className="card team-leave-calendar">
      <div className="calendar-head">
        <div>
          <h3>{safeT("team.calendarTitle", null, "İzin takvimi")}</h3>
          <div className="muted">{scopeLabel}</div>
        </div>
        <div className="calendar-nav">
          <button
            className="ghost"
            type="button"
            onClick={() => setCalendarStart(new Date(calendarStart.getFullYear(), calendarStart.getMonth() - 1, 1))}
          >
            {t("account.prev")}
          </button>
          <button
            className="ghost"
            type="button"
            onClick={() => {
              const now = new Date();
              setCalendarStart(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            {safeT("team.calendarToday", null, "Bugün")}
          </button>
          <div className="mono">{monthRangeLabel}</div>
          <button
            className="ghost"
            type="button"
            onClick={() => setCalendarStart(new Date(calendarStart.getFullYear(), calendarStart.getMonth() + 1, 1))}
          >
            {t("account.next")}
          </button>
        </div>
      </div>

      {error ? (
        <div className="muted" style={{ marginTop: 8 }}>
          {t("common.error")}: {error}
        </div>
      ) : loading ? (
        <div className="muted" style={{ marginTop: 8 }}>
          {t("common.loading")}
        </div>
      ) : null}

      <div className="legend">
        <span className="dot approved" /> {safeT("team.calendarApprovedLegend", null, "Onaylı izin")}
        <span className="dot holiday" /> {safeT("account.publicHoliday", null, "Resmi tatil")}
      </div>

      <div className="month-strip">
        {months.map((month) => {
          const weeks = monthMatrix(month.date.getFullYear(), month.date.getMonth());
          return (
            <div className="month-card" key={month.key}>
              <div className="month-name">{month.label}</div>
              <table className="calendar no-stack" aria-label={`Leave calendar ${month.label}`}>
                <thead>
                  <tr>
                    {weekdays.map((w) => (
                      <th key={w}>{w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((week, wi) => (
                    <tr key={wi}>
                      {week.map((d, di) => {
                        if (!d) return <td key={di} className="empty" />;
                        const key = toLocalDateKey(d);
                        const holiday = holidayMap.get(key);
                        const nonWorkday = isWeekend(d) || Boolean(holiday);
                        const countRaw = key ? countMap.get(key) || 0 : 0;
                        const count = nonWorkday ? 0 : countRaw;
                        const cls = count > 0 ? "approved" : "";
                        const title = holiday ? `${key} - ${holiday}` : key;
                        const offCountText =
                          count > 0
                            ? safeT("team.calendarOffCount", { count }, `${count} kişi izinli`)
                            : "";
                        const fullTitle = count > 0 ? `${title} - ${offCountText}` : title;

                        return (
                          <td
                            key={di}
                            className={`day ${cls} ${holiday ? "holiday" : ""} ${key === todayKey ? "today" : ""}`}
                            title={fullTitle}
                          >
                            <div className="num">{d.getDate()}</div>
                            {holiday && (
                              <div className="holiday-chip" title={holiday}>
                                {holidayShortLabel(holiday)}
                              </div>
                            )}
                            {count > 0 && <div className="leave-count">{count}</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamLeaveCalendarCard;
